import { map, MonoTypeOperatorFunction, pipe } from 'rxjs';

/**
 * Get the operation to compare two values
 * @example
 * getOperation(1, 2, '>') // false
 * getOperation(1, 2, '<') // true
 * getOperation(1, 2, '=') // false
 * getOperation(1, 2, '!=') // true
 * getOperation('John', 'john', 'like') // true
 */
export function getOperation(a: any, b: any, operator: '>' | '<' | '=' | '!=' | 'like') {
  switch (operator) {
    case '>':
      return a > b;
    case '<':
      return a < b;
    case '=':
      return a === b;
    case '!=':
      return a !== b;
    case 'like':
      return a.toString().toLowerCase().includes(b.toString().toLowerCase());
    default:
      return a === b;
  }
}

export type FilterFn = (item: any) => boolean;
export interface FilterLogicalOperator {
  operator: 'and' | 'or';
}

export type FilterArgs = FilterFn | FilterLogicalOperator;

/**
 * Filter an array of objects by passing an object with the properties you want to filter
 * @example
 *   items.filter(filterBy({ name: 'John', age: 20 }));
 */
export function filterBy<T>(filters: Partial<T>, operator: '>' | '<' | '=' | '!=' | 'like' = '='): FilterFn {
  return (item: T) => Object.keys(filters).every((key) => getOperation(item[key as keyof T], filters[key as keyof T], operator));
}

export function isLogicalOperator(obj: any): obj is FilterLogicalOperator {
  return obj && obj.operator;
}

export function filterMultiple(...args: FilterArgs[]): FilterFn {
  return (item: any) => {
    let operators: any[] = [];
    return args.reduce((acc, filter) => {
      if (isLogicalOperator(filter)) {
        operators.push(filter.operator);
        return acc;
      } else {
        if (operators.length > 0) {
          if (operators.pop() === 'or') {
            return acc || filter(item);
          }
        }
      }

      return acc && filter(item);
    }, true);
  };
}

export function filterPipe(...args: FilterFn[]): MonoTypeOperatorFunction<any> {
  return pipe(map((items) => items.filter(filterMultiple(...args))));
}
